<template>
  <!-- ═══════════ Kundenlinks (04.11.2026) ═══════════
       Links auf die Statusseite des Kunden mit direkt geöffneter Aktion (customerLink): Status, Bild ändern /
       Größe+Ausführung (Selbststart-Dialog), Storno (optional ohne Gebühr), Adresse, Nachricht. Je Zeile kopieren
       oder Mail in Kundensprache. Echte Editor-Links mit Token gibt es weiter im Menü der Task-Karte. -->
  <div class="ov-card cl">
    <div class="ov-card__head mb-3">
      <v-icon small class="ov-card__icon">mdi-link-variant</v-icon>
      Kundenlinks
    </div>

    <div v-for="row in statusRows" :key="row.kind" class="cl__row">
      <v-icon small class="cl__icon">{{ row.icon }}</v-icon>
      <div class="cl__main">
        <div class="cl__line">
          <span class="cl__label">{{ row.label }}</span>
          <span v-if="stateFor(row.kind)" :class="['cl__state', stateFor(row.kind).ok ? 'cl__state--ok' : 'cl__state--warn']" :title="stateFor(row.kind).title || ''">{{ stateFor(row.kind).text }}</span>
        </div>
        <div class="cl__hint" :title="row.hint">{{ row.hint }}</div>
      </div>
      <!-- Gebühren-Freigabe: signierter Zusatz im Link, der Storno-Dialog rechnet dann 0 € Gebühr -->
      <button
        v-if="row.kind === 'cancel' && cancelState && cancelState.ok"
        type="button"
        class="cl__fee"
        :class="{ 'cl__fee--on': feeWaived }"
        :title="feeWaived ? 'Link ohne Bearbeitungsgebühr (0 €) – klicken für Standard' : 'Standard: Gebühr fällt an – klicken für Link ohne Gebühr'"
        @click="feeWaived = !feeWaived"
      >
        <v-icon x-small class="mr-1">{{ feeWaived ? "mdi-gift-outline" : "mdi-cash" }}</v-icon>{{ feeWaived ? "ohne Gebühr" : "mit Gebühr" }}
      </button>
      <v-btn icon small :loading="busy === row.kind + ':create'" :disabled="!!busy" title="Link kopieren" @click="statusLink(row, 'create')">
        <v-icon small>mdi-content-copy</v-icon>
      </v-btn>
      <v-btn icon small :loading="busy === row.kind + ':mail'" :disabled="!!busy || !hasEmail" :title="hasEmail ? `Mail an ${order.customer.email}` : 'Kunde ohne E-Mail'" @click="statusLink(row, 'mail')">
        <v-icon small>mdi-email-send-outline</v-icon>
      </v-btn>
    </div>
    <div v-if="!hasEmail" class="ov-hint">Kunde ohne E-Mail-Adresse – nur Kopieren möglich.</div>
  </div>
</template>

<script>
import { httpsCallable } from "firebase/functions";
import { functions as fbFunctions } from "../../../../fb";
import { copyToClipboard } from "@/utils";
import { EventBus, events } from "../../../../event-bus";
import dayjs from "dayjs";

const STATUS_ROWS = [
  { kind: "status", icon: "mdi-truck-check-outline", label: "Statusseite", hint: "Bestellstatus + Sendungsverfolgung" },
  { kind: "edit", icon: "mdi-image-edit-outline", label: "Bild ändern", hint: "Selbststart: Foto, Text, Hintergrund · Produktion pausiert" },
  { kind: "editProduct", icon: "mdi-resize", label: "Größe / Ausführung", hint: "Selbststart: nur Größe, Material, Extras · Bild bleibt" },
  { kind: "cancel", icon: "mdi-cancel", label: "Stornieren", hint: "Selbststorno-Dialog, Bestätigung per Code" },
  { kind: "address", icon: "mdi-map-marker-outline", label: "Adresse ändern", hint: "Adressformular der Statusseite" },
  { kind: "contact", icon: "mdi-message-text-outline", label: "Nachricht", hint: "Kontaktformular mit Bestellbezug" },
];
const CANCEL_STATE = {
  eligible: { ok: true, text: "möglich" },
  off: { ok: false, text: "Selbststorno für diesen Shop aus" },
  capped: { ok: false, text: "Tagesdeckel erreicht" },
  pending: { ok: false, text: "läuft bereits" },
  awaiting_code: { ok: false, text: "wartet auf Code" },
  ticket: { ok: false, text: "liegt beim Support" },
  done: { ok: false, text: "bereits storniert" },
  produced: { ok: false, text: "in Produktion" },
  manual: { ok: false, text: "nur manuell (Status)" },
  error: { ok: false, text: "Prüfung fehlgeschlagen" },
};

export default {
  name: "CustomerLinksCard",
  props: {
    order: { type: Object, required: true },
  },
  data() {
    return {
      busy: null,
      loadingInfo: false,
      info: null,
      feeWaived: false,
      statusRows: STATUS_ROWS,
    };
  },
  computed: {
    hasEmail() {
      return !!(this.order && this.order.customer && this.order.customer.email);
    },
    cancelState() {
      const c = this.info && this.info.cancellation;
      if (!c) return null;
      const base = CANCEL_STATE[c.state] || { ok: false, text: c.state || "unbekannt" };
      if (!base.ok) return { ...base, title: c.error || "" };
      const fee = c.feeApplies ? `Gebühr ${c.fee && c.fee.amount != null ? `${c.fee.amount} ${c.fee.currency || ""}`.trim() : "Standard"}` : `gebührenfrei bis ${c.freeUntil ? dayjs(c.freeUntil).format("HH:mm") : "–"}`;
      return { ok: true, text: `möglich · ${fee}`, title: "Stand jetzt – der Dialog prüft beim Öffnen erneut" };
    },
  },
  watch: {
    "order.uid": {
      immediate: true,
      handler() {
        this.loadInfo();
      },
    },
  },
  methods: {
    async loadInfo() {
      if (!this.order || !this.order.uid) return;
      this.loadingInfo = true;
      try {
        const fn = httpsCallable(fbFunctions, "customerLink", { timeout: 30000 });
        const { data } = await fn({ action: "info", orderUid: String(this.order.uid) });
        this.info = data;
      } catch (e) {
        console.error("customerLink info:", e);
        this.info = { cancellation: { state: "error", error: e.message || String(e) } };
      } finally {
        this.loadingInfo = false;
      }
    },
    stateFor(kind) {
      if (kind === "cancel") return this.cancelState;
      if (kind === "edit" || kind === "editProduct") return this.editState(kind);
      return null;
    },
    /** Selbststart auf der Statusseite: Stand aus customerLinkInfo (Kontingent, Status, Teil-2-Schalter) */
    editState(kind) {
      const se = this.info && this.info.selfEdit;
      if (!se) return null;
      if (!se.enabled) return { ok: false, text: se.reason || "nicht möglich" };
      if (kind === "editProduct" && !se.productChange) return { ok: false, text: "Größe/Ausführung für diesen Shop aus" };
      return { ok: true, text: `möglich · ${se.startsLeft}/${se.maxStarts} Starts` };
    },
    /** Statusseiten-Links über customerLink (kein Token, Aktion als Parameter; Storno optional ohne Gebühr) */
    async statusLink(row, action) {
      const waived = row.kind === "cancel" && this.feeWaived;
      if (action === "mail" && !window.confirm(`${row.label}${waived ? " (ohne Gebühr)" : ""}: Mail an ${this.order.customer.email} senden?`)) return;
      this.busy = `${row.kind}:${action}`;
      try {
        const fn = httpsCallable(fbFunctions, "customerLink", { timeout: 60000 });
        const { data } = await fn({ action, orderUid: String(this.order.uid), kind: row.kind, feeWaived: waived });
        const suffix = data.feeWaived ? " (ohne Gebühr)" : "";
        if (action === "create") {
          await copyToClipboard(data.url);
          EventBus.$emit(events.SNACKBAR, { message: `${row.label}${suffix}: Link kopiert`, type: "success" });
        } else {
          EventBus.$emit(events.SNACKBAR, { message: `${row.label}${suffix}: Mail an ${data.mailTo} gesendet (${data.lang})`, type: "success" });
        }
      } catch (e) {
        console.error("Kundenlink:", e);
        EventBus.$emit(events.SNACKBAR, { message: `${row.label}: ${e.message || e}`, type: "error" });
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cl {
  &__row {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 0;
    border-top: 1px dashed rgba(0, 0, 0, 0.06);
    &:first-of-type {
      border-top: 0;
    }
    ::v-deep .v-btn--icon.v-size--small {
      width: 26px;
      height: 26px;
    }
  }
  &__icon {
    flex: 0 0 auto;
    color: rgba(0, 0, 0, 0.5);
  }
  &__main {
    flex: 1 1 auto;
    min-width: 0;
    line-height: 1.25;
  }
  &__line {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }
  &__label {
    font-size: 12.5px;
    font-weight: 500;
  }
  &__hint {
    font-size: 10.5px;
    color: rgba(0, 0, 0, 0.48);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__state {
    display: inline-block;
    padding: 0 5px;
    border-radius: 8px;
    font-size: 10px;
    font-weight: 600;
    line-height: 16px;
    &--ok {
      background: rgba(76, 175, 80, 0.12);
      color: #2e7d32;
    }
    &--warn {
      background: rgba(255, 152, 0, 0.14);
      color: #b26a00;
    }
  }
  &__fee {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    height: 20px;
    padding: 0 7px;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.14);
    background: #fff;
    font-size: 10.5px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.6);
    cursor: pointer;
    &--on {
      border-color: rgba(46, 125, 50, 0.4);
      background: rgba(76, 175, 80, 0.12);
      color: #2e7d32;
    }
  }
}
</style>
